import { Link } from 'react-router-dom';
import type { CheckBatch } from '../types';
import { formatDate, formatDateTime, formatMoney, plural } from '../format';

interface ApprovedStepProps {
  batch: CheckBatch;
  onBack: () => void;
}

/**
 * Step 4. The heading follows the number of included employees (2+ → batch, exactly 1 → employee). The text states what approval is: a record of
 * approval. Nothing here says a check was printed, mailed or paid.
 */
export function ApprovedStep({ batch, onBack }: ApprovedStepProps) {
  const a = batch.approval;
  if (!a) return null;
  const single = a.employeeCount === 1;
  const heading = single ? 'Employee Payslip Approved' : 'Payment Batch Approved';

  return (
    <section className="wc-approved" aria-labelledby="wc-approved-title">
      <div className="wc-approved__badge" aria-hidden="true">✓</div>
      <h3 id="wc-approved-title">{heading}</h3>
      <p className="wc-approved__text" role="status">
        {single ? 'The employee’s payslip' : 'The payment batch'} has been approved. Users who can view payment batches have been notified.
        {a.isPreview && ' This is a preview: no check was printed, mailed or paid.'}
      </p>
      <p className="wc-batch-id" data-testid="batch-reference">Batch ID: {a.reference}</p>
      <dl className="wc-cards wc-cards--narrow">
        <div><dt>Payment Method</dt><dd>Write Check</dd></div>
        <div><dt>Pay Date</dt><dd>{formatDate(batch.payDate)}</dd></div>
        <div><dt>{single ? 'Employee to be paid' : 'Employees to be paid'}</dt><dd data-testid="approved-count">{plural(a.employeeCount, 'Employee', 'Employees')}</dd></div>
        <div><dt>Check {a.checkNumbers.length === 1 ? 'Number' : 'Numbers'}</dt><dd data-testid="approved-numbers">{a.checkNumbers.join(', ')}</dd></div>
        <div className="wc-cards__wide">
          <dt>Total Amount</dt>
          <dd><span data-testid="approved-total">{formatMoney(a.checkTotal)}</span> <span className="wc-chip wc-chip--ok">Approved</span></dd>
        </div>
        <div className="wc-cards__wide wc-cards__split">
          <div><dt>Approved By</dt><dd>{a.approvedByEmail}</dd></div>
          <div><dt>Approved At</dt><dd>{formatDateTime(a.approvedAtUtc)}</dd></div>
        </div>
      </dl>
      <div className="wc-approved__actions">
        <button type="button" className="wc-btn wc-btn--outline" onClick={onBack}>Back to Pay Employees</button>
        <Link className="wc-btn wc-btn--green" to="/payroll/employee-payslips">View Employee Payslips</Link>
      </div>
    </section>
  );
}
